import _ from 'lodash'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SettingId } from '@fusion-advantage/backcompat'
import { PreviewBranchInfo } from '../dal2/selectPreviewBranchInfo'
import { AppSliceSettings, DefaultAppSliceSettings, restoreAppSliceSettings } from '../lib/setting'
import { AppEnvironment, cid24, RootRouteContentType, RootRouteDashboardContent, RootRouteFormContent, SyncStrategy, WorkItemAction } from '@fusion-advantage/formlib'
import { getDefaultSyncStrategy } from '../lib/appInfo'
import { AppThunk } from './appStore'
import { resetAppFormState } from './actions'

// "loading" (but easier to grep for)
export interface AppLdng {
	login: boolean
	syncCloudToDevice: boolean
	form: boolean
	apiAudit: boolean
	apiDashboardApptSendReminder: boolean
	apiDashboardGenerateUrls: boolean
	apiDashboardSubmitFeedback: boolean
	apiDevDestroyEncounters: boolean
	apiMgrTokenEcho: boolean
}

export enum AppModal {
	CancelEncounter = 'CancelEncounter',
	Database = 'Database',
	DevToken = 'DevToken',
	Location = 'Location',
	Log = 'Log',
	ManageEncounters = 'ManageEncounters',
	GenerateMeetingLinks = 'GenerateMeetingLinks',
	Sync = 'Sync',
	Feedback = 'Feedback',
}

export enum DocgenStatus {
	Loading = 'loading',
	Success = 'success',
	Error = 'error',
}

export type AppSliceState = AppSliceSettings & {
	ldng: Partial<AppLdng>
	booted: boolean
	environment: AppEnvironment
	frontendSelectorOpen: boolean
	barcodeScannerOpen: boolean
	content: RootRouteDashboardContent|RootRouteFormContent
	// we will only show one modal at a time, but this way we can line them up
	// in display order so when you close one, another will be displayed
	modals: AppModal[]
	backgroundTaps: number
	sessionExpiredAt: number|null
	simulateOffline: boolean
	syncStrategy: SyncStrategy
	renderer: number|null
	// set by the /preview route before its first sync, so preview mode
	// (no writes, no background sync) is in force from the start;
	// the branch info is read from the branch's flowmap once downloaded
	preview: {
		branchGuid: string
		branchInfo: PreviewBranchInfo|null
	}|null
	docgen: {
		status: DocgenStatus.Loading|DocgenStatus.Error
	} | {
		status: DocgenStatus.Success
		height: number
	}
}

const initialState: AppSliceState = {
	...DefaultAppSliceSettings,
	ldng: {},
	booted: false,
	environment: AppEnvironment.Production,
	frontendSelectorOpen: false,
	barcodeScannerOpen: false,
	content: {
		type: RootRouteContentType.Dashboard,
	},
	modals: [],
	backgroundTaps: 0,
	sessionExpiredAt: null,
	simulateOffline: false,
	syncStrategy: getDefaultSyncStrategy(),
	renderer: null,
	preview: null,
	docgen: {
		status: DocgenStatus.Loading,
	},
}

const appSlice = createSlice({
	name: 'app',
	initialState,
	reducers: {
		setLdng(state, action: PayloadAction<Partial<AppLdng>>) {
			state.ldng = {
				...state.ldng,
				...action.payload,
			}
		},
		setBooted(state) {
			state.booted = true
		},
		setAppEnvironment(state, action: PayloadAction<AppEnvironment>) {
			state.environment = action.payload
		},
		setFrontendSelectorOpen(state, action: PayloadAction<boolean>) {
			state.frontendSelectorOpen = action.payload
		},
		setBarcodeScannerOpen(state, action: PayloadAction<boolean>) {
			state.barcodeScannerOpen = action.payload
		},
		internalSetAppContent(state, action: PayloadAction<RootRouteDashboardContent|RootRouteFormContent>) {
			state.content = action.payload
		},
		pushAppModal(state, action: PayloadAction<AppModal>) {
			if(!state.modals.includes(action.payload)) {
				state.modals.push(action.payload)
			}
		},
		unshiftAppModal(state, action: PayloadAction<AppModal>) {
			if(!state.modals.includes(action.payload)) {
				state.modals.unshift(action.payload)
			}
		},
		dismissAppModal(state, action: PayloadAction<AppModal>) {
			state.modals = state.modals.filter(modal => modal !== action.payload)
		},
		incrementBackgroundTaps(state) {
			state.backgroundTaps++
		},
		setClientId(state, action: PayloadAction<string>) {
			state.clientId = action.payload
		},
		setBearerToken(state, action: PayloadAction<AppSliceState["bearerToken"]>) {
			state.bearerToken = action.payload
		},
		setSessionExpiredAt(state, action: PayloadAction<number|null>) {
			state.sessionExpiredAt = action.payload
		},
		toggleTrainingMode(state) {
			state.isTrainingMode = !state.isTrainingMode
		},
		setForceDevRenderer(state, action: PayloadAction<boolean>) {
			state.forceDevRenderer = action.payload
		},
		setSimulateOffline(state, action: PayloadAction<boolean>) {
			state.simulateOffline = action.payload
		},
		setSyncStrategy(state, action: PayloadAction<SyncStrategy>) {
			state.syncStrategy = action.payload
		},
		setRenderer(state, action: PayloadAction<number|null>) {
			state.renderer = action.payload
		},
		startPreview(state, action: PayloadAction<string>) {
			state.preview = {
				branchGuid: action.payload,
				branchInfo: null,
			}
		},
		setPreviewBranchInfo(state, action: PayloadAction<PreviewBranchInfo>) {
			if(state.preview) {
				state.preview.branchInfo = action.payload
			}
		},
		setDocgenStatus(state, action: PayloadAction<AppSliceState["docgen"]>) {
			state.docgen = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(restoreAppSliceSettings, (state, action) => {
			const state2 = {
				...state,
				...action.payload,
			}
			// if there's already a bearerToken (e.g. set in print url by docgen)
			// do not override it
			if(state[SettingId.BearerToken]) {
				state2[SettingId.BearerToken] = state[SettingId.BearerToken]
			}
			return state2
		})
		builder.addCase(resetAppFormState, (state) => {
			return {
				...state,
				renderer: null,
			}
		})
	}
})

export const {
	setLdng,
	setBooted,
	setAppEnvironment,
	setFrontendSelectorOpen,
	setBarcodeScannerOpen,
	pushAppModal,
	unshiftAppModal,
	dismissAppModal,
	incrementBackgroundTaps,
	setClientId,
	setBearerToken,
	setSessionExpiredAt,
	toggleTrainingMode,
	setForceDevRenderer,
	setSimulateOffline,
	setSyncStrategy,
	setRenderer,
	startPreview,
	setPreviewBranchInfo,
	setDocgenStatus,
} = appSlice.actions
export default appSlice.reducer

const {
	internalSetAppContent
} = appSlice.actions

export const setAppContent = (payload: RootRouteDashboardContent|Omit<RootRouteFormContent, 'renderInstanceId'>): AppThunk<Promise<void>> => async (dispatch, getState) => {
	if(payload.type === RootRouteContentType.Form) {
		const sync = getState().sync
		if(sync.startedAt && !sync.endedAt) {
			if(!sync.canCancel) {
				alert('System is busy; please try again in a few seconds.')
				return
			}
		}
		if(payload.action === WorkItemAction.ViewFormAsDocgen) {
			dispatch(setDocgenStatus({status: DocgenStatus.Loading }))
		}
		const content: RootRouteFormContent = {
			...payload,
			// generate new instance id
			renderInstanceId: cid24('i'),
		}
		dispatch(internalSetAppContent(content))
	} else {
		dispatch(internalSetAppContent(payload))
	}
}
